<template>
  <List
    class="tag-list"
    titleI18nKey="admin.crm.title.tag.management"
    :loading="loading"
    :listData="listData"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :pagination="pagination"
    @create="handleCreate"
    @pagination-change="handlePaginationChange"
  >
    <template #filter>
      <el-input
        v-model="search.keywords"
        clearable
        :placeholder="$t('admin.crm.placeholder.keywords')"
        @change="getTagList"
        @keypress.enter.native="getTagList"
      >
        <template #prefix>
          <i class="el-input__icon el-icon-search"></i>
        </template>
      </el-input>
    </template>
    <template #status="{ row }">
      <el-switch
        v-model="row.status"
        class="status-switch"
        :width="34"
        @change="handleSwitch(row)"
      ></el-switch>
    </template>
    <template #page-bottom>
      <DialogWrapper
        titleI18nKey="admin.crm.message.tag.deleteConfirm"
        saveBtnLabelI18nKey="common.btn.confirm"
        closeOnCancel
        :loading="dialog.loading"
        :show.sync="dialog.show"
        @save="handleDelete"
      ></DialogWrapper>
    </template>
  </List>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { CONDITIONS_RELATION_TYPE, CONDITION_RELATION, CONDITION_RULES } from '@/constant/crm'
import List from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { isEn } from '@/i18n'

export default {
  components: {
    List,
    DialogWrapper
  },
  data () {
    return {
      loading: false,
      listData: [],
      tableColumns: [
        {
          width: 100,
          labelI18nKey: 'admin.crm.label.tag.tagName',
          dataKey: 'name'
        },
        {
          width: 100,
          labelI18nKey: 'admin.crm.label.tag.tagCnName',
          dataKey: 'cn_name'
        },
        {
          width: 200,
          labelI18nKey: 'admin.crm.label.tag.judgingCriteria',
          dataKey: 'condition',
          formatter: (data, row) => {
            return this.generateJudgingCriteria(data, row)
          }
        },
        {
          width: 65,
          labelI18nKey: 'common.label.status',
          slotName: 'status',
          dataKey: 'status'
        }
      ],
      actionBtn: {
        edit: {
          onclick: this.handleEdit
        },
        delete: {
          onclick: this.openDialog
        }
      },
      search: {
        keywords: null
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      dialog: {
        show: false,
        loading: false,
        data: null,
        dataIndex: null
      }
    }
  },
  computed: {

  },
  created () {
    this.getTagList()
  },
  methods: {
    ...mapActions('crm', ['searchTag', 'updateTag', 'deleteTag']),
    async getTagList () {
      this.loading = true
      const resp = await this.searchTag({
        is_company_tag: 1,
        need_disabled: 1,
        tag_category: ['manual', 'smart'].join(','),
        keywords: this.search.keywords,
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      })
      if (resp) {
        this.listData = _.map(resp.records, item => {
          return {
            ...item,
            status: !item.is_disabled
          }
        })
        this.pagination.total = resp.total
      }
      this.loading = false
    },
    generateJudgingCriteria (condition, tag) {
      let judgingCriteria = this.$t(`admin.crm.option.tag.${tag.match_condition_rule}`)
      if (tag.match_condition_rule !== CONDITION_RULES.no) {
        judgingCriteria = judgingCriteria.concat(isEn() ? ': ' : '：')
      }
      return judgingCriteria.concat(this.generateConditionDisplayValue(condition))
    },
    generateConditionDisplayValue (condition) {
      const conditionsDisplayValue = []
      JSON.parse(condition).forEach(item => {
        const items = []
        items.push(this.$t(`admin.crm.condition.name.${item.name}`))
        if (item.relationType === CONDITIONS_RELATION_TYPE.CONTAINS) {
          items.push(this.$t(`admin.crm.condition.relation.${item.relation}`))
          items.push(this.convertConditionValue(item))
        } else {
          items.push(this.convertConditionValue(item))
          items.push(this.$t(`admin.crm.condition.relation.${item.relation}`))
        }
        conditionsDisplayValue.push(items.join(' '))
      })

      return conditionsDisplayValue.join('; ')
    },
    convertConditionValue (condition) {
      let conditionValue = condition.value
      if (CONDITIONS_RELATION_TYPE.CONTAINS === condition.relationType) {
        const joinKey = this.$t('common.or')
        if (condition.name === 'gender') {
          conditionValue = _.map(conditionValue, item => {
            if (item === 'F') {
              return this.$t('common.gender.female')
            } else if (item === 'N') {
              return this.$t('common.gender.nonBinary')
            } else if (item === 'P') {
              return this.$t('common.gender.preferNotToDisclose')
            } else {
              return this.$t('common.gender.male')
            }
          }).join(joinKey)
        } else {
          conditionValue = conditionValue.join(joinKey)
        }
      } else if (CONDITION_RELATION.BETWEEN === condition.relation) {
        conditionValue = `${conditionValue[0]} - ${conditionValue[1]}`
      }

      return conditionValue
    },
    handleTagTaskList () {
      this.$router.push({ name: 'tagTaskList' })
    },
    handleCreate () {
      this.$router.push({ name: 'addTag' })
    },
    handleEdit (rowData) {
      this.$router.push({
        name: 'editTag',
        params: {
          tagId: rowData.id
        }
      })
    },
    async handleSwitch (rowData) {
      this.loading = true
      try {
        rowData.is_disabled = !rowData.status
        const tag = { ...rowData }
        delete tag.status
        await this.updateTag(tag)
      } finally {
        this.loading = false
      }
    },
    openDialog (rowData, index) {
      this.dialog.show = true
      this.dialog.data = rowData
      this.dialog.dataIndex = index
    },
    async handleDelete () {
      this.dialog.loading = true
      try {
        await this.deleteTag(this.dialog.data.id)
        this.listData.splice(this.dialog.dataIndex, 1)
      } finally {
        this.dialog.show = false
        this.dialog.loading = false
        this.dialog.data = null
        this.dialog.dataIndex = null
      }
    },
    handlePaginationChange () {
      this.getTagList()
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-list {

  .status-switch {
    ::v-deep {
      &.is-checked  {
        .el-switch__core {
          &::after {
            margin-left: -15px;
          }
        }
      }
      .el-switch__core {
        height: 16px;

        &::after {
          width: 14px;
          height: 14px;
          top: 0;
        }
      }
    }
  }

  .dialog-footer {
    .delete-btn {
      margin-left: 35px;
      float: left;
    }
  }

  .tag-pagination {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
